import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { formatTokenAmount, formatUSDAmount } from 'utils/FormatNumber';
import { useQuery } from '@tanstack/react-query';
import Big from 'big.js';
import {
  DWalletService,
  dWalletServiceKeys,
} from 'api/services/httpServices/DWalletService';

import {
  RowLabel,
  RowLabelledCheckBox,
  RowLabelledInput,
} from '../../../components/RowLabelledInput';
import { PresetButton } from '../../../components/PresetButton';
import { useState } from 'react';
import Tooltip from 'modules/shared-components/tooltip';
import {
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  MinusIcon,
  PlusIcon,
  ScaleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import {
  MobileExpandableCard,
  MobileExpandCardTrigger,
} from 'modules/shared-components/MobileExpandableCard';
import GasIcon from '../../../../../assets/img/icons/gas-icon.svg';
import { isNotNil } from 'modules/utils/isNotNil';
import {
  chainAsset,
  chainDefaultBuyAmountInBaseAsset,
  chainHasBribe,
  chainHasMemPool,
  chainPriorityGasFeeTitle,
  chainPriorityGasFeeTooltip,
  gasPrioritySuffix,
} from 'api/types/httpsTypes/d-wallets';
import { DcaOrderType, VolatilityBotFormValues } from '../schema';
import NumberInput from '../../../components/inputs/number-input';
import Checkbox from '../../../components/inputs/Checkbox';
import { DexCard } from '../../../components/cards';
import { useChainInfo } from 'api/hooks/useChainInfo';
import Button from 'modules/shared-components/button/button';
import { fromAmount } from '../../../components/chains/units';
import { IconButton } from 'modules/shared-components/button/IconButton';

function DcaOrders() {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<VolatilityBotFormValues>();

  const dcaOrders = watch('botSettings.entries.limitOrders') as
    | DcaOrderType
    | undefined;
  const chain = watch('tradingSettings.chain');
  const slippage = watch('tradingSettings.slippage');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'botSettings.entries.limitOrders',
    keyName: 'key',
  });

  return (
    <div className="space-y-1">
      {fields.map((row, index) => (
        <div className="flex xxs:space-x-1" key={row.key}>
          <Controller
            name={`botSettings.entries.limitOrders.${index}.priceDeviationPercentage`}
            control={control}
            defaultValue={'' as any}
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                {...field}
                inputSize="small"
                prefix="-"
                suffix="%"
                error={error?.message}
                hideErrorMessage
                value={field.value as number}
              />
            )}
          />

          <Controller
            name={`botSettings.entries.limitOrders.${index}.amount`}
            control={control}
            defaultValue={'' as any}
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                {...field}
                inputSize="small"
                suffix={chainAsset(chain)}
                error={error?.message}
                hideErrorMessage
                value={field.value}
              />
            )}
          />

          <Controller
            name={`botSettings.entries.limitOrders.${index}.slippage`}
            control={control}
            defaultValue={'' as any}
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                {...field}
                inputSize="small"
                suffix="%"
                error={error?.message}
                hideErrorMessage
                value={field.value}
              />
            )}
          />

          {index === 0 ? (
            <IconButton
              className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:text-dex-white-secondary hover:text-dex-white xxs:p-1 xxs:rounded"
              type="button"
              onClick={() =>
                append({
                  amount:
                    !dcaOrders || dcaOrders.length === 0
                      ? fromAmount(
                          chainDefaultBuyAmountInBaseAsset(chain),
                          chain,
                          'weiToEther'
                        )
                      : Big(dcaOrders[dcaOrders.length - 1]?.amount ?? 0)
                          .mul(1.5)
                          .toNumber(),
                  priceDeviationPercentage:
                    !dcaOrders || dcaOrders.length === 0
                      ? 5
                      : Big(
                          dcaOrders[dcaOrders.length - 1]
                            ?.priceDeviationPercentage ?? 0
                        )
                          .plus(5)
                          .toNumber(),
                  slippage:
                    !dcaOrders || dcaOrders.length === 0
                      ? slippage
                      : Big(dcaOrders[dcaOrders.length - 1]?.slippage ?? 0)
                          .mul(1.2)
                          .toNumber(),
                })
              }
            >
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
            </IconButton>
          ) : (
            <IconButton
              className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:text-dex-white-secondary hover:text-dex-white xxs:p-1 xxs:rounded"
              type="button"
              onClick={() => remove(index)}
            >
              <MinusIcon className="h-5 w-5" aria-hidden="true" />
            </IconButton>
          )}
        </div>
      ))}
      {errors?.botSettings?.entries?.limitOrders?.message && (
        <div className="text-red-500">
          {errors.botSettings?.entries.limitOrders.message}
        </div>
      )}
    </div>
  );
}

export function BuyAmount({ isEdit }: { isEdit: boolean | undefined }) {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<VolatilityBotFormValues>();
  const chain = watch('tradingSettings.chain');
  const [openBoxDca, setOpenBoxDca] = useState(
    watch('botSettings.entries.isDcaActive')
  );
  const [openWalletSettingsBox, setOpenWalletSettingsBox] = useState(false);
  const selectedWalletId = watch('tradingSettings.walletId');

  const { data } = useQuery({
    queryFn: () => DWalletService.getWallets(),
    queryKey: dWalletServiceKeys.getWallets(),
  });

  const errorsGas =
    errors?.tradingSettings?.maxBuyGasPriority ||
    errors?.tradingSettings?.antiMev ||
    errors?.tradingSettings?.slippage;
  const selectedWallet = data?.find((x) => x.id === selectedWalletId);
  const buyOrderSettings = watch('tradingSettings');
  const chainPrice = useChainInfo(chain)?.price ?? 0;
  const amount = watch('tradingSettings.buyAmount');
  const dcaOrders = watch('botSettings.entries.limitOrders');
  const slippage = watch('tradingSettings.slippage');
  const isDcaActive = watch('botSettings.entries.isDcaActive');
  const DEFAULT_LIMIT_ORDER = {
    amount:
      amount && Big(amount ?? 0).gt(0)
        ? amount
        : fromAmount(
            chainDefaultBuyAmountInBaseAsset(chain),
            chain,
            'weiToEther'
          ),
    priceDeviationPercentage: 10,
    slippage,
  };

  return (
    <MobileExpandableCard
      mobileLabel="Buy Settings"
      renderTrigger={({ open }) => (
        <MobileExpandCardTrigger onClick={open}>
          <div className="flex justify-between items-center text-sm">
            <div className="flex space-x-1 items-center">
              {errors?.tradingSettings?.buyAmount ? (
                <div className="flex space-x-1 items-center">
                  <ExclamationCircleIcon className="xxs:text-red-500 xxs:w-6 h-6" />
                  <div>Missing Information</div>
                </div>
              ) : (
                <>
                  <div className="xxs:text-dex-white-secondary">Buy:</div>
                  <>
                    <div className="xxs:text-dex-white">
                      {buyOrderSettings.buyAmount ? (
                        <>{formatTokenAmount(buyOrderSettings.buyAmount)} </>
                      ) : (
                        <div className="xxs:text-dex-white-secondary">--</div>
                      )}
                    </div>
                    <div className="xxs:text-dex-white-secondary">|</div>
                    {isDcaActive ? (
                      <div className="flex space-x-1 items-center">
                        <div className="font-bold">DCA</div>
                        <CheckCircleIcon className="w-5 h-5 xxs:text-dex-white" />
                      </div>
                    ) : (
                      <div className="flex space-x-1 items-center">
                        <div className="font-bold">DCA</div>
                        <XCircleIcon className="w-5 h-5 xxs:text-dex-white" />
                      </div>
                    )}

                    <div className="flex space-x-1 items-center">
                      <div className="xxs:text-dex-white-secondary">|</div>
                      <div className="flex xxs:space-x-1 items-center">
                        <img className="h-4 w-4" alt="gas icon" src={GasIcon} />
                        {buyOrderSettings?.maxBuyGasPriority ? (
                          <div className="xxs:text-dex-white">
                            {formatTokenAmount(
                              buyOrderSettings?.maxBuyGasPriority
                            )}{' '}
                          </div>
                        ) : (
                          <div className="xxs:text-dex-white-secondary">--</div>
                        )}
                      </div>
                      <div className="xxs:text-dex-white-secondary">|</div>
                      <div className="flex xxs:space-x-1 items-center">
                        <ArrowTrendingDownIcon className="h-4 w-4 xxs:text-dex-white-secondary" />
                        <div className="xxs:text-dex-white">
                          {isNotNil(buyOrderSettings?.slippage) ? (
                            <>{buyOrderSettings?.slippage}%</>
                          ) : (
                            <div className="xxs:text-dex-white-secondary">
                              --
                            </div>
                          )}
                        </div>
                      </div>

                      {chainHasMemPool(chain) && <div>|</div>}
                      {chainHasMemPool(chain) && (
                        <div>
                          {buyOrderSettings.antiMev ? (
                            <div className="flex space-x-1 items-center">
                              <div className="xxs:text-dex-white-secondary">
                                MEV
                              </div>
                              <CheckCircleIcon className="w-5 h-5 xxs:text-dex-white" />
                            </div>
                          ) : (
                            <div className="flex space-x-1 items-center">
                              <div className="xxs:text-dex-white-secondary">
                                MEV
                              </div>
                              <XCircleIcon className="w-5 h-5 xxs:text-dex-white" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                </>
              )}
            </div>
          </div>
        </MobileExpandCardTrigger>
      )}
    >
      <DexCard size="xs">
        <Controller
          name="tradingSettings.buyAmount"
          defaultValue={'' as any}
          control={control}
          render={({ field, fieldState: { error } }) => {
            const value =
              typeof field.value === 'number'
                ? field.value
                : Number(field.value) || 0;
            return (
              <NumberInput
                {...field}
                label="Buy Amount"
                tooltip={`The amount (in ${chainAsset(
                  chain
                )}) that will be used to execute the buy transaction.`}
                suffix={<>{chainAsset(chain)}</>}
                extraLabel={
                  chainPrice ? (
                    <>
                      ${formatUSDAmount(Big(value).mul(chainPrice)?.toString())}
                    </>
                  ) : undefined
                }
                error={error?.message}
              />
            );
          }}
        />
        <div className="flex xxs:gap-1 mb-1">
          {quickOptions.map((a) => (
            <PresetButton
              key={a}
              type="button"
              disabled={!selectedWallet}
              onClick={() => {
                if (selectedWallet?.chainBalance) {
                  setValue(
                    'tradingSettings.buyAmount',
                    Big(selectedWallet.chainBalance)
                      .times(a)
                      .div(100)
                      .round(4, Big.roundDown)
                      .toNumber(),
                    {
                      shouldDirty: true,
                    }
                  );
                }
              }}
            >
              {a}%
            </PresetButton>
          ))}
        </div>
        <div className="xxs:space-y-2 lg:space-y-0">
          <div>
            <RowLabelledCheckBox
              label={
                <RowLabel
                  label="Dollar Cost Averaging (DCA)"
                  isError={!!errors?.botSettings?.entries?.limitOrders}
                  tooltip="Buy more if the price drops beyond your expectations. DCA can help lower your average buy price and increase the chances of making a profit if there’s a rebound."
                />
              }
              openBox={openBoxDca}
              setOpenBox={
                watch('botSettings.entries.isDcaActive')
                  ? () => setOpenBoxDca(!openBoxDca)
                  : undefined
              }
              className="xxs:py-1"
            >
              <Controller
                name={'botSettings.entries.isDcaActive'}
                defaultValue={'' as any}
                control={control}
                render={({ field }) => {
                  return (
                    <Checkbox
                      className="xxs:w-5 xxs:h-5 lg:w-4 lg:h-4"
                      checked={
                        field.value === undefined
                          ? false
                          : (field.value as boolean)
                      }
                      onClick={field.onChange}
                      onChange={() => {
                        if (!field.value) {
                          setOpenBoxDca(true);
                          setValue('botSettings.entries.limitOrders', [
                            DEFAULT_LIMIT_ORDER,
                          ]);
                          setValue(
                            'botSettings.entries.autoRetryEnabled',
                            true
                          );
                          setValue(
                            'botSettings.entries.autoRetry.buy.active',
                            true
                          );
                          setValue(
                            'botSettings.entries.autoRetry.buy.nRetries',
                            1
                          );
                          setValue('botSettings.entries.expirationInHours', 48);
                        } else {
                          if (watch('botSettings.entries.autoRetryEnabled')) {
                            setValue(
                              'botSettings.entries.autoRetry.buy.active',
                              false
                            );
                            setValue(
                              'botSettings.entries.autoRetry.buy.nRetries',
                              0
                            );
                          }

                          setValue('botSettings.entries.limitOrders', [
                            DEFAULT_LIMIT_ORDER,
                          ]);
                          setOpenBoxDca(false);
                        }
                      }}
                      id="safetyMeasures.liquidityRange"
                    />
                  );
                }}
              />
            </RowLabelledCheckBox>
            {openBoxDca && (
              <div className="space-y-1 border-tailwind border-dex-black-700 xxs:p-2 rounded xxs:my-2">
                <div className="flex justify-between xxs:gap-1 dark:text-black-50 pb-0.5 xxs:text-xs">
                  <div className="w-full">
                    <Tooltip text="The price deviation, calculated from the price at which the first order is executed, determines the price at which an extra buy will be triggered.">
                      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
                        {' '}
                        Price Deviation
                      </div>
                    </Tooltip>
                  </div>
                  <div className="w-full flex xxs:space-x-1 items-center">
                    <Tooltip
                      className="w-full"
                      text="The extra amount that will be purchased (DCA) once the price deviation is reached."
                    >
                      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
                        {' '}
                        Amount
                      </div>
                    </Tooltip>
                    <div>
                      {isEdit &&
                        watch('botSettings.entries.limitOrdersScale') && (
                          <Button
                            type="button"
                            className="xxs:px-1 xxs:py-0.5"
                            onClick={() => {
                              const a = watch('tradingSettings.buyAmount') ?? 0;
                              const limitOrdersScale =
                                watch('botSettings.entries.limitOrdersScale') ??
                                [];

                              if (
                                !isNaN(a) &&
                                Big(a).gt(0) &&
                                limitOrdersScale.length > 0
                              ) {
                                if (limitOrdersScale) {
                                  limitOrdersScale.forEach((scale) => {
                                    dcaOrders.forEach((dca, index) => {
                                      if (dca.id === scale.id) {
                                        setValue(
                                          `botSettings.entries.limitOrders.${index}.amount`,
                                          Big(amount ?? 0)
                                            .mul(scale.amount ?? 0)
                                            .toNumber()
                                        );
                                      }
                                    });
                                  });
                                }
                              }
                            }}
                            variant="dexNeutral"
                          >
                            <div>
                              <div className="xxs:space-x-0.5 items-center flex xxs:text-xs normal-case">
                                <ScaleIcon className="w-4 h-4 lg:hidden" />
                                <div className="xxs:hidden lg:block">
                                  Auto-scale
                                </div>
                                <Tooltip.Info
                                  className="w-3 h-3 xxs:hidden lg:block"
                                  text="Auto-scale your DCA orders based on new buy amount."
                                />
                              </div>
                            </div>
                          </Button>
                        )}
                    </div>
                  </div>
                  <div className="w-full">
                    <Tooltip
                      className="w-full"
                      text="The slippage applied to the DCA level."
                    >
                      <div className=" xxs:text-dex-white-secondary hover:text-dex-white">
                        {' '}
                        Slippage
                      </div>
                    </Tooltip>
                  </div>
                  <div className="invisible w-[30px] h-[10px]">button</div>
                </div>
                <DcaOrders />

                <Controller
                  name={'botSettings.entries.expirationInHours'}
                  defaultValue={'' as any}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <NumberInput
                        name="Expiration"
                        tooltip="The expiration time (in hours) after which the limit order will be considered expired"
                        value={field.value as number}
                        onChange={(event) =>
                          field.onChange(event.currentTarget.value)
                        }
                        suffix={<>hr</>}
                        label="Expiration time"
                        error={error?.message}
                      />
                    );
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div>
          <button
            type="button"
            onClick={() => setOpenWalletSettingsBox(!openWalletSettingsBox)}
            className={twMerge(
              ' m-0 p-0 font-normal normal-case justify-start w-full',
              errorsGas ? 'text-red-500' : ''
            )}
          >
            <div className="flex justify-between items-center xxs:text-dex-white-secondary hover:text-dex-white">
              <div className="flex space-x-1 items-center">
                {errorsGas && (
                  <ExclamationCircleIcon className="xxs:text-red-500 xxs:w-4 h-4" />
                )}
                <Tooltip text="Set-up buy gas priority, slippage, and MEV protection.">
                  <div className="text-sm ">
                    Gas, Slippage
                    {chainHasMemPool(chain) && (
                      <span>, and MEV protection</span>
                    )}
                  </div>
                </Tooltip>
              </div>
              <div className="">
                {openWalletSettingsBox ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </div>
            </div>
          </button>
          {openWalletSettingsBox && (
            <>
              <RowLabelledInput
                label={
                  <RowLabel
                    label={chainPriorityGasFeeTitle(
                      watch('tradingSettings.chain')
                    )}
                    tooltip={chainPriorityGasFeeTooltip(
                      watch('tradingSettings.chain')
                    )}
                  />
                }
                className="xxs:py-1"
              >
                <Controller
                  name="tradingSettings.maxBuyGasPriority"
                  control={control}
                  defaultValue={'' as any}
                  render={({ field, fieldState: { error } }) => (
                    <NumberInput
                      containerClassName="xxs:w-[108px]"
                      suffix={<>{gasPrioritySuffix(chain)}</>}
                      value={field.value as number}
                      onChange={(event) => {
                        setValue(
                          'tradingSettings.isMaxBuyGasPriorityEdited',
                          true
                        );

                        field.onChange(event.currentTarget.value);
                      }}
                      error={error?.message}
                      name={''}
                    />
                  )}
                />
              </RowLabelledInput>

              <RowLabelledInput
                label={
                  <RowLabel
                    label="Slippage"
                    tooltip="The slippage threshold for executing the buy transaction."
                  />
                }
                className="xxs:py-1"
              >
                <Controller
                  name={'tradingSettings.slippage'}
                  defaultValue={'' as any}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <NumberInput
                        {...field}
                        id="tradingSettings.slippage"
                        name="tradingSettings.slippage"
                        containerClassName="xxs:w-[108px]"
                        suffix={<>%</>}
                        error={error?.message}
                      />
                    );
                  }}
                />
              </RowLabelledInput>
              {chainHasMemPool(chain) && (
                <RowLabelledInput
                  label={
                    <RowLabel
                      label="MEV Protection"
                      tooltip="When active, the buy transaction will be executed as a private transaction. This will avoid your transaction to be front-run or sandwiched."
                    />
                  }
                  className="xxs:py-1"
                >
                  <Controller
                    name="tradingSettings.antiMev"
                    control={control}
                    defaultValue={'' as any}
                    render={({ field }) => (
                      <div className="flex items-center xxs:w-[108px] xxs:gap-2">
                        <Checkbox
                          checked={field.value as boolean}
                          onClick={field.onChange}
                          id="tradingSettings.antiMev"
                        />
                        <Checkbox.Label htmlFor="anti-mev">
                          Enabled
                        </Checkbox.Label>
                      </div>
                    )}
                  />
                </RowLabelledInput>
              )}
              {chainHasBribe(chain) && watch('tradingSettings.antiMev') && (
                <RowLabelledInput
                  label={
                    <RowLabel
                      label="Bribe Amount"
                      tooltip="Bribe is used to make transaction faster when using anti-mev."
                    />
                  }
                  className="xxs:py-1"
                >
                  <Controller
                    name="tradingSettings.bribe"
                    control={control}
                    defaultValue={'' as any}
                    render={({ field, fieldState: { error } }) => (
                      <NumberInput
                        containerClassName="xxs:w-[108px]"
                        suffix={<>{gasPrioritySuffix(chain)}</>}
                        value={field.value as number}
                        onChange={(event) => {
                          field.onChange(event.currentTarget.value);
                        }}
                        error={error?.message}
                        name={''}
                      />
                    )}
                  />
                </RowLabelledInput>
              )}
            </>
          )}
        </div>
      </DexCard>
    </MobileExpandableCard>
  );
}

const quickOptions = [1, 3, 5, 10];
